import { formatMgrs, formatUtm } from "./coordinateProjection"

const ABBREVIATIONS = Object.freeze({
  dd: "DD",
  "hemisphere-dd": "HDD",
  ddm: "DDM",
  dms: "DMS",
  "signed-dms": "Signed DMS",
  "compact-dd": "Compact DD",
  "compact-hemisphere-dd": "Compact HDD",
  "compact-ddm": "Compact DDM",
  "compact-dms": "Compact DMS",
  "compact-signed-dms": "Compact Signed DMS",
  radians: "Radians",
  utm: "UTM",
  mgrs: "MGRS",
})

const OPTION_DATA = [
  ["dd", "Decimal Degrees (DD)", "Decimal Degrees", "-32.928300, 151.781700"],
  ["hemisphere-dd", "Hemisphere Degrees", "Hemisphere Degrees", "32.928300° S, 151.781700° E"],
  ["ddm", "Degrees Decimal Minutes (DDM)", "Degrees Decimal Minutes", "32° 55.698' S, 151° 46.902' E"],
  ["dms", "Degrees Minutes Seconds (DMS)", "Degrees Minutes Seconds", "32° 55' 41.88\" S, 151° 46' 54.12\" E"],
  ["signed-dms", "Signed DMS", "Signed DMS", "-32° 55' 41.88\", 151° 46' 54.12\""],
  ["compact-dd", "Compact Decimal Degrees", "Compact DD", "-32.928300,151.781700"],
  ["compact-hemisphere-dd", "Compact Hemisphere Degrees", "Compact HDD", "32.928300S,151.781700E"],
  ["compact-ddm", "Compact DDM", "Compact DDM", "3255.698S,15146.902E"],
  ["compact-dms", "Compact DMS", "Compact DMS", "325542S,1514654E"],
  ["compact-signed-dms", "Compact Signed DMS", "Compact Signed DMS", "-325541.88,1514654.12"],
  ["radians", "Radians", "Radians", "-0.5747072521 rad, 2.6490904093 rad"],
  ["utm", "UTM", "UTM", "Zone 56H Easting: 386096 Northing: 6356003"],
  ["mgrs", "MGRS", "MGRS", "56H LJ 86095 56003"],
]

export const COORDINATE_FORMAT_OPTIONS = Object.freeze(OPTION_DATA.map(
  ([value, label, shortLabel, sample]) => Object.freeze({
    value,
    label,
    shortLabel,
    abbreviation: ABBREVIATIONS[value],
    sample,
  }),
))

const VALID_FORMATS = new Set(COORDINATE_FORMAT_OPTIONS.map(({ value }) => value))

export function normalizeCoordinateFormat(value) {
  return VALID_FORMATS.has(value) ? value : "ddm"
}

export function coordinateFormatAbbreviation(value) {
  return ABBREVIATIONS[normalizeCoordinateFormat(value)]
}

function hemisphere(value, isLat) {
  return isLat ? (value < 0 ? "S" : "N") : value < 0 ? "W" : "E"
}

function ddmParts(value) {
  const totalMinutes = Math.round(Math.abs(value) * 60 * 1000) / 1000
  const degrees = Math.floor(totalMinutes / 60)
  return { degrees, minutes: totalMinutes - degrees * 60 }
}

function dmsParts(value, precision = 2) {
  const scale = 10 ** precision
  const totalSeconds = Math.round(Math.abs(value) * 3600 * scale) / scale
  const degrees = Math.floor(totalSeconds / 3600)
  const remainder = totalSeconds - degrees * 3600
  const minutes = Math.floor(remainder / 60)
  return { degrees, minutes, seconds: remainder - minutes * 60 }
}

export function toDDM(value, isLat) {
  const { degrees, minutes } = ddmParts(value)
  return `${degrees}° ${minutes.toFixed(3)}' ${hemisphere(value, isLat)}`
}

export function formatDms(value, isLat, signed = false) {
  const { degrees, minutes, seconds } = dmsParts(value)
  const prefix = signed && value < 0 ? "-" : ""
  const suffix = signed ? "" : ` ${hemisphere(value, isLat)}`
  return `${prefix}${degrees}° ${minutes}' ${seconds.toFixed(2)}"${suffix}`
}

function formatCompactDdm(value, isLat) {
  const { degrees, minutes } = ddmParts(value)
  const degreeWidth = isLat ? 2 : 3
  return `${String(degrees).padStart(degreeWidth, "0")}${minutes.toFixed(3).padStart(6, "0")}${hemisphere(value, isLat)}`
}

function formatCompactDms(value, isLat, signed = false) {
  const precision = signed ? 2 : 0
  const { degrees, minutes, seconds } = dmsParts(value, precision)
  const degreeWidth = isLat ? 2 : 3
  const secondWidth = signed ? 5 : 2
  const prefix = signed && value < 0 ? "-" : ""
  const suffix = signed ? "" : hemisphere(value, isLat)
  return `${prefix}${String(degrees).padStart(degreeWidth, "0")}${String(minutes).padStart(2, "0")}${seconds.toFixed(precision).padStart(secondWidth, "0")}${suffix}`
}

export function formatCoordinatePair(lat, lng, format = "ddm") {
  switch (normalizeCoordinateFormat(format)) {
    case "dd":
      return `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`
    case "hemisphere-dd":
      return `${Math.abs(lat).toFixed(6)}° ${hemisphere(lat, true)}, ${Math.abs(lng).toFixed(6)}° ${hemisphere(lng, false)}`
    case "dms":
      return `${formatDms(lat, true)}, ${formatDms(lng, false)}`
    case "signed-dms":
      return `${formatDms(lat, true, true)}, ${formatDms(lng, false, true)}`
    case "compact-dd":
      return `${Number(lat).toFixed(6)},${Number(lng).toFixed(6)}`
    case "compact-hemisphere-dd":
      return `${Math.abs(lat).toFixed(6)}${hemisphere(lat, true)},${Math.abs(lng).toFixed(6)}${hemisphere(lng, false)}`
    case "compact-ddm":
      return `${formatCompactDdm(lat, true)},${formatCompactDdm(lng, false)}`
    case "compact-dms":
      return `${formatCompactDms(lat, true)},${formatCompactDms(lng, false)}`
    case "compact-signed-dms":
      return `${formatCompactDms(lat, true, true)},${formatCompactDms(lng, false, true)}`
    case "radians":
      return `${((lat * Math.PI) / 180).toFixed(10)} rad, ${((lng * Math.PI) / 180).toFixed(10)} rad`
    case "utm":
      return formatUtm(lat, lng)
    case "mgrs":
      return formatMgrs(lat, lng)
    default:
      return `${toDDM(lat, true)}, ${toDDM(lng, false)}`
  }
}
